
import React, { useEffect, useRef, useState } from "react";

interface GoldSparkleCanvasProps {
  targetRef?: React.RefObject<HTMLElement>;
  particleCount?: number;
  padding?: number;
  extraWidth?: number;
  extraHeight?: number;
  borderWidth?: number;
  minBorderWidth?: number;
  maxBorderWidth?: number;
  showDebugBorder?: boolean;
  starProbability?: number;
  outerMargin?: number;
  innerPadding?: number;
}

interface Particle {
  x: number;
  y: number;
  size: number;
  speedX: number;
  speedY: number;
  opacity: number;
  colorIndex: number;
  life: number;
  maxLife: number;
  glowSize: number;
  isStar: boolean;
  starType: number;
  rotation: number;
  rotationSpeed: number;
  borderThickness: number;
  thicknessDirection: number;
}

const goldColors = [
  { hue: 45, sat: 95, light: 75, name: 'bright-gold' },
  { hue: 43, sat: 90, light: 85, name: 'pale-gold' },
  { hue: 48, sat: 95, light: 70, name: 'rich-gold' },
  { hue: 36, sat: 90, light: 78, name: 'warm-gold' },
  { hue: 50, sat: 95, light: 88, name: 'light-gold' },
  { hue: 38, sat: 85, light: 80, name: 'soft-gold' },
  { hue: 44, sat: 100, light: 75, name: 'pure-gold' },
  { hue: 52, sat: 90, light: 83, name: 'cream-gold' },
];

// Create these functions outside component to avoid recreation on each render
const drawSparkle = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  rotation: number = 0
) => {
  const outerRadius = size * 1.5;
  const innerRadius = size * 0.5;
  
  ctx.beginPath();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  
  for (let i = 0; i < 4; i++) {
    const angle = (i * Math.PI / 2);
    ctx.lineTo(Math.cos(angle) * outerRadius, Math.sin(angle) * outerRadius);
    ctx.lineTo(Math.cos(angle + Math.PI/4) * innerRadius, Math.sin(angle + Math.PI/4) * innerRadius);
  }
  
  ctx.closePath();
  ctx.fill();
  ctx.resetTransform();
};

const drawTinyStars = (
  ctx: CanvasRenderingContext2D,
  x: number, 
  y: number,
  size: number
) => {
  const count = 3 + Math.floor(Math.random() * 3);
  
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * Math.PI * 2;
    const distance = size * 1.4;
    const dotX = x + Math.cos(angle) * distance;
    const dotY = y + Math.sin(angle) * distance;
    const dotSize = size * 0.6 * (0.6 + Math.random() * 0.5);
    
    ctx.beginPath();
    ctx.arc(dotX, dotY, dotSize, 0, Math.PI * 2);
    ctx.fill();
  }
  
  ctx.beginPath();
  ctx.arc(x, y, size * 0.85, 0, Math.PI * 2);
  ctx.fill();
};

const drawDiamond = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  rotation: number = 0
) => {
  const outerRadius = size * 2.0;
  
  ctx.beginPath();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  
  ctx.moveTo(0, -outerRadius);
  ctx.lineTo(outerRadius, 0);
  ctx.lineTo(0, outerRadius);
  ctx.lineTo(-outerRadius, 0);
  
  ctx.closePath();
  ctx.fill();
  ctx.resetTransform();
};

const drawHexagon = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  rotation: number = 0
) => {
  const radius = size * 1.4;
  
  ctx.beginPath();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  
  for (let i = 0; i < 6; i++) {
    const angle = (i * Math.PI) / 3;
    const px = radius * Math.cos(angle);
    const py = radius * Math.sin(angle);
    
    if (i === 0) {
      ctx.moveTo(px, py);
    } else {
      ctx.lineTo(px, py);
    }
  }
  
  ctx.closePath();
  ctx.fill();
  ctx.resetTransform();
};

const drawStar = (
  ctx: CanvasRenderingContext2D, 
  x: number, 
  y: number, 
  size: number, 
  spikes: number = 5,
  rotation: number = 0
) => {
  const outerRadius = size * 1.5;
  const innerRadius = size / 2.2;
  const step = Math.PI / spikes;
  
  ctx.beginPath();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  
  for (let i = 0; i < spikes * 2; i++) {
    const radius = i % 2 === 0 ? outerRadius : innerRadius;
    const angle = i * step;
    
    if (i === 0) {
      ctx.moveTo(radius, 0);
    } else {
      ctx.lineTo(radius * Math.cos(angle), radius * Math.sin(angle));
    }
  }
  
  ctx.closePath();
  ctx.fill();
  ctx.resetTransform();
};

const drawStarBurst = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  rotation: number = 0
) => {
  const outerRadius = size * 2.2;
  const innerRadius = size * 0.3;
  const spikes = 8;
  const step = Math.PI / spikes;
  
  ctx.beginPath();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  
  for (let i = 0; i < spikes * 2; i++) {
    const radius = i % 2 === 0 ? outerRadius : innerRadius;
    const angle = i * step;
    
    if (i === 0) {
      ctx.moveTo(radius, 0);
    } else {
      ctx.lineTo(radius * Math.cos(angle), radius * Math.sin(angle));
    }
  }
  
  ctx.closePath();
  ctx.fill();
  ctx.resetTransform();
};

// Main component
const GoldSparkleCanvas: React.FC<GoldSparkleCanvasProps> = ({
  targetRef,
  particleCount = 200, // Reduced default for better performance
  padding = 12,
  extraWidth = 15,
  extraHeight = 15,
  borderWidth = 4,
  minBorderWidth = 2, 
  maxBorderWidth = 6,
  showDebugBorder = false,
  starProbability = 0.5,
  outerMargin = 0,
  innerPadding = 0
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const requestRef = useRef<number>();
  const particlesRef = useRef<Particle[]>([]);
  const rectRef = useRef<DOMRect | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isReady, setIsReady] = useState(false);

  // Initialize particles once the component is mounted and we have dimensions
  const initParticles = (rect: DOMRect) => {
    try {
      const particles: Particle[] = [];
      
      for (let i = 0; i < particleCount; i++) {
        let x: number, y: number;
        
        const edge = Math.floor(Math.random() * 4);
        
        const initialBorderThickness = Math.random() * (maxBorderWidth - minBorderWidth) + minBorderWidth;
        
        const canvasWidth = rect.width + (padding * 2);
        const canvasHeight = rect.height + (padding * 2);
        
        const borderOffset = initialBorderThickness * 6.0;
        
        const effectivePadding = padding + innerPadding;
        
        if (edge === 0) {
          x = effectivePadding + Math.random() * (rect.width - innerPadding * 2);
          y = effectivePadding + (borderOffset * Math.random() * 0.6);
        } else if (edge === 1) {
          x = padding + rect.width - innerPadding - (borderOffset * Math.random() * 0.6);
          y = effectivePadding + Math.random() * (rect.height - innerPadding * 2);
        } else if (edge === 2) {
          x = effectivePadding + Math.random() * (rect.width - innerPadding * 2);
          y = padding + rect.height - innerPadding - (borderOffset * Math.random() * 0.6);
        } else {
          x = effectivePadding + (borderOffset * Math.random() * 0.6);
          y = effectivePadding + Math.random() * (rect.height - innerPadding * 2);
        }
        
        const size = Math.random() * 2.5 + 0.8;
        
        const isStar = Math.random() < starProbability;
        const starType = isStar ? Math.floor(Math.random() * 6) : 0;
        
        const colorIndex = Math.floor(Math.random() * goldColors.length);
        
        particles.push({
          x,
          y,
          size,
          speedX: (Math.random() - 0.5) * 0.2, 
          speedY: (Math.random() - 0.5) * 0.2,
          opacity: Math.random() * 0.7 + 0.3,
          colorIndex,
          life: 0,
          maxLife: Math.random() * 300 + 200,
          glowSize: Math.random() * 6 + 2,
          isStar,
          starType,
          rotation: Math.random() * Math.PI * 2,
          rotationSpeed: (Math.random() - 0.5) * 0.02,
          borderThickness: initialBorderThickness,
          thicknessDirection: Math.random() > 0.5 ? 1 : -1
        });
      }
      
      return particles;
    } catch (error) {
      console.error("Error initializing particles:", error);
      return [];
    }
  };

  // Check if a particle is in the border area
  const isInBorderArea = (
    x: number, 
    y: number, 
    width: number, 
    height: number, 
    currentThickness: number
  ): boolean => {
    try {
      const adjustedPadding = padding + innerPadding;
      
      if (
        x < adjustedPadding || 
        x > width + padding * 2 - innerPadding || 
        y < adjustedPadding || 
        y > height + padding * 2 - innerPadding
      ) {
        return false;
      }
      
      const adjustedX = x - padding;
      const adjustedY = y - padding;
      
      const outerBuffer = 0;
      
      if (
        adjustedX >= -outerBuffer && 
        adjustedX <= width + outerBuffer && 
        adjustedY >= -outerBuffer && 
        adjustedY <= height + outerBuffer
      ) {
        const borderMultiplier = 0.8;
        
        const isOutsideInnerRect = !(
          adjustedX >= (currentThickness * borderMultiplier) + innerPadding && 
          adjustedX <= width - (currentThickness * borderMultiplier) - innerPadding && 
          adjustedY >= (currentThickness * borderMultiplier) + innerPadding && 
          adjustedY <= height - (currentThickness * borderMultiplier) - innerPadding
        );
        
        return isOutsideInnerRect;
      }
      
      return false;
    } catch (error) {
      console.error("Error in isInBorderArea:", error);
      return false;
    }
  };

  // Update all particles positions and properties
  const updateParticles = (particles: Particle[], width: number, height: number): Particle[] => {
    try {
      return particles.map(particle => {
        particle.borderThickness += 0.01 * particle.thicknessDirection;
        
        if (particle.borderThickness > maxBorderWidth) {
          particle.borderThickness = maxBorderWidth;
          particle.thicknessDirection = -1;
        } else if (particle.borderThickness < minBorderWidth) {
          particle.borderThickness = minBorderWidth;
          particle.thicknessDirection = 1;
        }
        
        const prevX = particle.x;
        const prevY = particle.y;
        
        particle.x += particle.speedX;
        particle.y += particle.speedY;
        particle.life += 0.4;
        particle.rotation += particle.rotationSpeed;
        
        const actualWidth = width - (padding * 2);
        const actualHeight = height - (padding * 2);
        
        if (
          particle.life > particle.maxLife ||
          !isInBorderArea(particle.x, particle.y, actualWidth, actualHeight, particle.borderThickness)
        ) {
          const edge = Math.floor(Math.random() * 4);
          const borderOffset = particle.borderThickness * 6.0;
          const effectivePadding = padding + innerPadding;
          
          if (edge === 0) {
            particle.x = effectivePadding + Math.random() * (actualWidth - innerPadding * 2);
            particle.y = effectivePadding + (borderOffset * Math.random() * 0.6);
          } else if (edge === 1) {
            particle.x = padding + actualWidth - innerPadding - (borderOffset * Math.random() * 0.6);
            particle.y = effectivePadding + Math.random() * (actualHeight - innerPadding * 2);
          } else if (edge === 2) {
            particle.x = effectivePadding + Math.random() * (actualWidth - innerPadding * 2);
            particle.y = padding + actualHeight - innerPadding - (borderOffset * Math.random() * 0.6);
          } else {
            particle.x = effectivePadding + (borderOffset * Math.random() * 0.6);
            particle.y = effectivePadding + Math.random() * (actualHeight - innerPadding * 2);
          }
          
          particle.life = 0;
          particle.opacity = Math.random() * 0.7 + 0.3;
          particle.isStar = Math.random() < starProbability;
          particle.starType = particle.isStar ? Math.floor(Math.random() * 6) : 0;
          particle.size = Math.random() * 2.5 + 0.8;
          particle.rotation = Math.random() * Math.PI * 2;
          particle.colorIndex = Math.floor(Math.random() * goldColors.length);
        } else {
          const totalWidth = width - (innerPadding * 2);
          const totalHeight = height - (innerPadding * 2);
          const minX = padding + innerPadding;
          const minY = padding + innerPadding;
          const maxX = padding + totalWidth;
          const maxY = padding + totalHeight;
          
          if (particle.x < minX) {
            particle.x = minX;
            particle.speedX = Math.abs(particle.speedX) * 0.8;
          } else if (particle.x > maxX) {
            particle.x = maxX;
            particle.speedX = -Math.abs(particle.speedX) * 0.8;
          }
          
          if (particle.y < minY) {
            particle.y = minY;
            particle.speedY = Math.abs(particle.speedY) * 0.8;
          } else if (particle.y > maxY) {
            particle.y = maxY;
            particle.speedY = -Math.abs(particle.speedY) * 0.8;
          }
        }
        
        return particle;
      });
    } catch (error) {
      console.error("Error updating particles:", error);
      return particles;
    }
  };

  // Draw all particles
  const drawParticles = (
    ctx: CanvasRenderingContext2D,
    particles: Particle[],
    width: number,
    height: number
  ) => {
    try {
      ctx.clearRect(0, 0, width, height);
      
      particles.forEach(particle => {
        const { x, y, size, opacity, colorIndex, glowSize, isStar, starType, rotation, borderThickness } = particle;
        
        if (x < 0 || x > width || y < 0 || y > height) {
          return;
        }
        
        const colorInfo = goldColors[colorIndex];
        
        const adjustedGlowSize = glowSize * (borderThickness / borderWidth) * 1.1;
        
        const gradient = ctx.createRadialGradient(
          x, y, 0,
          x, y, adjustedGlowSize
        );
        
        const baseColor = `hsla(${colorInfo.hue}, ${colorInfo.sat}%, ${colorInfo.light}%, ${opacity * 1.1})`;
        const glowColor = `hsla(${colorInfo.hue}, ${colorInfo.sat}%, ${colorInfo.light}%, 0)`;
        
        gradient.addColorStop(0, baseColor);
        gradient.addColorStop(1, glowColor);
        
        ctx.fillStyle = gradient;
        
        if (isStar) {
          if (starType === 0) {
            drawStar(ctx, x, y, size * 2.0, 5, rotation);
          } else if (starType === 1) {
            drawSparkle(ctx, x, y, size * 1.6, rotation);
          } else if (starType === 2) {
            drawTinyStars(ctx, x, y, size * 1.2);
          } else if (starType === 3) {
            drawDiamond(ctx, x, y, size * 1.4, rotation);
          } else if (starType === 4) {
            drawHexagon(ctx, x, y, size * 1.4, rotation);
          } else {
            drawStarBurst(ctx, x, y, size * 1.3, rotation);
          }
        } else {
          ctx.beginPath();
          ctx.arc(x, y, adjustedGlowSize, 0, Math.PI * 2);
          ctx.fill();
          
          ctx.beginPath();
          const coreHue = colorInfo.hue + (Math.random() * 6 - 3);
          const coreSat = Math.min(colorInfo.sat + 10, 100);
          const coreLight = Math.min(colorInfo.light + 15, 100);
          ctx.fillStyle = `hsla(${coreHue}, ${coreSat}%, ${coreLight}%, ${opacity * 1.3})`;
          ctx.arc(x, y, size * 0.9, 0, Math.PI * 2);
          ctx.fill();
          
          ctx.beginPath();
          ctx.fillStyle = `rgba(255, 255, 255, ${opacity * 0.8})`;
          ctx.arc(x - size * 0.25, y - size * 0.25, size * 0.35, 0, Math.PI * 2);
          ctx.fill();
        }
      });
    } catch (error) {
      console.error("Error drawing particles:", error);
    }
  };

  // Update canvas dimensions based on target element size
  const updateDimensions = () => {
    try {
      if (!canvasRef.current || !targetRef?.current) return;
      
      const canvas = canvasRef.current;
      const targetElement = targetRef.current;
      const rect = targetElement.getBoundingClientRect();
      
      // Only update if dimensions actually changed
      if (rectRef.current && 
          rectRef.current.width === rect.width && 
          rectRef.current.height === rect.height) {
        return;
      }
      
      canvas.width = rect.width + (padding * 2) + extraWidth;
      canvas.height = rect.height + (padding * 2) + extraHeight;
      
      rectRef.current = rect;
      
      if (!isInitialized) {
        particlesRef.current = initParticles(rect);
        setIsInitialized(true);
        setIsReady(true);
      }
    } catch (error) {
      console.error("Error updating dimensions:", error);
    }
  };

  // Animation loop
  const animate = () => {
    try {
      if (!canvasRef.current || !rectRef.current || !isReady) return;
      
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      
      if (!ctx) return;
      
      particlesRef.current = updateParticles(
        particlesRef.current, 
        canvas.width, 
        canvas.height
      );
      
      drawParticles(ctx, particlesRef.current, canvas.width, canvas.height);
      
      requestRef.current = requestAnimationFrame(animate);
    } catch (error) {
      console.error("Error in animation loop:", error);
      // Try to recover by restarting animation loop
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
        requestRef.current = requestAnimationFrame(animate);
      }
    }
  };

  // Setup and cleanup
  useEffect(() => {
    if (!targetRef?.current) return;
    
    let isComponentMounted = true;
    
    const initTimer = setTimeout(() => {
      if (!isComponentMounted) return;
      
      try {
        updateDimensions();
        
        if (isReady) {
          requestRef.current = requestAnimationFrame(animate);
        }
      } catch (error) {
        console.error("Error initializing animation:", error);
      }
    }, 100);
    
    // Use debounced resize handler for better performance
    let resizeTimer: number;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        if (isComponentMounted) {
          updateDimensions();
        }
      }, 150);
    };
    
    window.addEventListener("resize", handleResize);
    
    return () => {
      isComponentMounted = false;
      clearTimeout(initTimer);
      clearTimeout(resizeTimer);
      window.removeEventListener("resize", handleResize);
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [targetRef, isReady]);

  return (
    <div 
      style={{
        position: "absolute",
        top: -padding - (extraHeight / 2) - outerMargin,
        left: -padding - (extraWidth / 2) - outerMargin,
        width: `calc(100% + ${padding * 2 + extraWidth + (outerMargin * 2)}px)`,
        height: `calc(100% + ${padding * 2 + extraHeight + (outerMargin * 2)}px)`,
        pointerEvents: "none",
        zIndex: 250,
        overflow: "visible",
        border: showDebugBorder ? "3px solid #FF0000" : "none",
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          top: outerMargin,
          left: outerMargin,
          width: `calc(100% - ${outerMargin * 2}px)`,
          height: `calc(100% - ${outerMargin * 2}px)`,
          pointerEvents: "none",
          zIndex: 250,
          overflow: "visible",
        }}
      />
    </div>
  );
};

export default GoldSparkleCanvas;
